import * as React from 'react';
import { Layers3, Loader2 } from 'lucide-react';
import type { PositionChartGroupingPolicy } from '../models/PositionChartGrouping';

export const PositionChartGroupingSettings: React.FC<{
  policy: PositionChartGroupingPolicy;
  canWrite: boolean;
  isSaving: boolean;
  error: unknown;
  onSave: (policy: PositionChartGroupingPolicy) => Promise<unknown>;
}> = ({ policy, canWrite, isSaving, error, onSave }) => {
  const [draft, setDraft] = React.useState(policy);
  React.useEffect(() => setDraft(policy), [policy]);
  const changed = JSON.stringify(draft) !== JSON.stringify(policy);

  return (
    <details className="border-b border-slate-200 px-4 py-2">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-semibold text-slate-700">
        <Layers3 className="size-3.5" />
        Position card grouping
      </summary>
      <div className="mt-3 space-y-3 pb-2 text-xs">
        <p className="text-[10px] leading-4 text-slate-500">
          Supervisor is always part of the grouping key. Grouping changes the shared folder view only; it never merges Position records.
        </p>
        <label className="block text-[11px] font-medium text-slate-600">
          Card model
          <select
            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
            value={draft.mode}
            disabled={!canWrite || isSaving}
            onChange={(event) => setDraft((current) => ({
              ...current,
              mode: event.target.value as PositionChartGroupingPolicy['mode'],
            }))}
          >
            <option value="grouped">Group equivalent seats</option>
            <option value="individual">One card per Position</option>
          </select>
        </label>
        {draft.mode === 'grouped' ? (
          <>
            <label className="block text-[11px] font-medium text-slate-600">
              Match role by
              <select
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-800"
                value={draft.roleKey}
                disabled={!canWrite || isSaving}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  roleKey: event.target.value as PositionChartGroupingPolicy['roleKey'],
                }))}
              >
                <option value="title">Position title</option>
                <option value="job_code">Job code (title fallback)</option>
              </select>
            </label>
            <label className="flex items-center justify-between gap-3 text-[11px] text-slate-600">
              Keep departments separate
              <input
                type="checkbox"
                checked={draft.includeDepartment}
                disabled={!canWrite || isSaving}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  includeDepartment: event.target.checked,
                }))}
              />
            </label>
            <label className="flex items-center justify-between gap-3 text-[11px] text-slate-600">
              Keep locations separate
              <input
                type="checkbox"
                checked={draft.includeLocation}
                disabled={!canWrite || isSaving}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  includeLocation: event.target.checked,
                }))}
              />
            </label>
          </>
        ) : null}
        <button
          type="button"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-3 text-xs font-medium text-white disabled:bg-slate-300"
          disabled={!canWrite || isSaving || !changed}
          onClick={() => void onSave(draft)}
        >
          {isSaving ? <Loader2 className="size-3.5 animate-spin" /> : null}
          Apply to folder
        </button>
        {error ? (
          <p className="text-[10px] text-red-700" role="alert">
            The grouping setting could not be saved. Try again.
          </p>
        ) : null}
      </div>
    </details>
  );
};
